import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Bell, Flame, DollarSign, AlertTriangle, TrendingUp, Bot } from "lucide-react";
import { toast } from "sonner";

const KIND_ICON = { hot_lead: Flame, payment: DollarSign, churn: AlertTriangle, opportunity: TrendingUp, escalation: Bot };
const SEVERITY_COLOR = {
  opportunity: "bg-warning-soft text-warning",
  revenue: "bg-success-soft text-success",
  risk: "bg-danger-soft text-danger",
  alert: "bg-info-soft text-info",
};

export default function Alerts() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = async () => setRows((await api.get("/alerts")).data);
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.post(`/alerts/${id}/read`);
    toast.success("Marked as read");
    load();
  };

  const filtered = filter === "all" ? rows : rows.filter(r => r.category === filter);
  const unread = rows.filter(r => !r.read).length;

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="alerts-page">
      <div className="flex items-center gap-3 mb-6">
        <Bell className="h-6 w-6 text-ai" />
        <div>
          <h1 className="font-display text-3xl font-bold text-ink">AI Alerts & Notifications</h1>
          <p className="text-sm text-meta mt-1"><span className="text-ai font-bold">{unread} unread</span> · AI surfaces what needs your attention</p>
        </div>
      </div>

      <div className="mb-5 inline-flex rounded-full bg-white border border-line p-1 shadow-sm">
        {[
          { id: "all", label: "All Alerts" },
          { id: "opportunities", label: "Opportunities" },
          { id: "payments", label: "Payments" },
          { id: "system", label: "System" },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full transition focus-ring ${filter === f.id ? "ashra-gradient-bg text-white shadow-sm" : "text-meta hover:text-ink"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.map((a) => {
          const Icon = KIND_ICON[a.kind] || Bell;
          return (
            <div key={a.id} className={`rounded-2xl bg-white border ${a.read ? "border-line" : "border-violet-300 shadow-sm"} p-4 flex items-center gap-3 hover:shadow-md transition`} data-testid={`alert-${a.id}`}>
              <span className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${SEVERITY_COLOR[a.severity] || "bg-zinc-100 text-meta"}`}>
                <Icon className="h-4 w-4" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-ink">{a.title}</p>
                <p className="text-xs text-meta">{a.detail}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[11px] text-meta-soft">{new Date(a.created_at).toLocaleString()}</p>
                {!a.read && <button onClick={() => markRead(a.id)} className="mt-1 text-xs text-ai font-semibold hover:underline">Mark read</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
